import { useEffect, useMemo, useRef, useState, type ComponentType } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "~/lib/utils";
import { useConversationContext } from "~/pages/manifold-nextgen/components/conversations/ConversationContext";
import { ArtifactShell } from "~/pages/manifold-nextgen/components/conversations/artifacts/ArtifactShell";
import { FileArtifactBody } from "~/pages/manifold-nextgen/components/conversations/artifacts/FileArtifactBody";
import {
  companionIdsFromPreview,
  filenameFromPreview,
  hasFileSuffix,
  urlFromPreview,
} from "~/pages/manifold-nextgen/components/conversations/artifacts/inlineViewerPreview";
import {
  artifactPreviewByIdUrl,
  type ArtifactScope,
} from "~/pages/manifold-nextgen/components/conversations/artifacts/useArtifactPreview";
import { useInlineViewerUrl } from "~/pages/manifold-nextgen/components/conversations/artifacts/useInlineViewerUrl";
import type { ArtifactPreview } from "~/services/artifacts";
import { apiClient } from "~/utils/apiClient";

// One file artifact as a viewer sees it. `text()` and `bytes()` download the
// whole object under the viewer's size cap, once for the inline and expanded
// views together; viewers that read by range (igv.js for bigWig) take `url`
// instead.
export interface FileSource {
  name: string;
  url: string;
  text(): Promise<string>;
  bytes(): Promise<Uint8Array<ArrayBuffer>>;
  // Files read together with this one (a WFDB header's signal and annotation
  // files), by name, and the presigned URL of one, from that file's own
  // preview in the viewer's scope.
  companions: readonly string[];
  companion(name: string): Promise<string>;
}

export interface MountedViewer {
  dispose(): void;
}

export type MountViewer = (container: HTMLElement, file: FileSource) => Promise<MountedViewer>;

export interface FileViewerSpec {
  suffixes: readonly string[];
  maxBytes: number;
  loadingLabel: string;
  // Height of the inline (unexpanded) viewer.
  heightClass?: string;
  // Dynamic import of the module that owns the viewer library, so each
  // library stays in its own lazy chunk.
  load: () => Promise<{ mount: MountViewer }>;
}

export interface FileViewer {
  matches(preview: ArtifactPreview): boolean;
  Artifact: ComponentType<{ preview: ArtifactPreview }>;
}

// Builds the artifact card for a viewer that draws one file into a DOM
// element: the shared fetch-under-a-cap, loading, error and teardown handling,
// with the library-specific drawing left to `spec.load`.
export function defineFileViewer(spec: FileViewerSpec): FileViewer {
  const Viewer = ({ file, className }: { file: FileSource | null; className?: string }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
      const container = containerRef.current;
      if (!container) {
        return;
      }

      let cancelled = false;
      let mounted: MountedViewer | null = null;

      const show = async () => {
        try {
          setIsLoading(true);
          setError(null);
          if (!file) {
            throw new Error("No content URL available for this artifact.");
          }
          const { mount } = await spec.load();
          if (cancelled) {
            return;
          }

          // Whatever a failed mount left behind; a disposed viewer removes its own.
          container.replaceChildren();
          const created = await mount(container, file);
          if (cancelled) {
            created.dispose();
            return;
          }
          mounted = created;
        } catch (err) {
          if (!cancelled) {
            console.error(`Failed to render ${file?.name ?? "artifact"}:`, err);
            setError(err instanceof Error ? err.message : "Unknown error");
          }
        } finally {
          if (!cancelled) {
            setIsLoading(false);
          }
        }
      };

      show();

      return () => {
        cancelled = true;
        mounted?.dispose();
      };
    }, [file]);

    return (
      <>
        {error && (
          <div className="flex h-48 items-center justify-center rounded-md border border-border bg-muted/40 px-4 text-center text-sm text-muted-foreground">
            {error}
          </div>
        )}
        {/* Stays mounted while an error shows, so a later file can draw into it. */}
        <div
          className={cn(
            "relative w-full",
            error ? "hidden" : (className ?? spec.heightClass ?? "h-[450px]")
          )}
        >
          {isLoading && !error && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-background">
              <div className="text-center">
                <Loader2 className="mx-auto mb-2 size-8 animate-spin text-muted-foreground" />
                <p className="text-sm text-muted-foreground">{spec.loadingLabel}</p>
              </div>
            </div>
          )}
          <div
            ref={containerRef}
            className={cn(
              "relative h-full w-full overflow-auto rounded-md",
              isLoading && "invisible"
            )}
          />
        </div>
      </>
    );
  };

  const Artifact = ({ preview }: { preview: ArtifactPreview }) => {
    const name = filenameFromPreview(preview);
    const url = useInlineViewerUrl(preview);
    const { conversationId, shareToken, isAdmin } = useConversationContext();
    // A string key, since the preview's object is rebuilt on every fetch.
    const companionKey = JSON.stringify(companionIdsFromPreview(preview));
    const file = useMemo(
      () =>
        url
          ? fileSource(name, url, spec.maxBytes, JSON.parse(companionKey), {
              conversationId,
              shareToken,
              isAdmin,
            })
          : null,
      [name, url, companionKey, conversationId, shareToken, isAdmin]
    );
    return (
      <ArtifactShell preview={preview} expandedContent={<Viewer file={file} className="h-full" />}>
        <FileArtifactBody preview={preview} />
        <div className="mt-3">
          <Viewer file={file} />
        </div>
      </ArtifactShell>
    );
  };

  return {
    matches: (preview) => hasFileSuffix(preview, spec.suffixes),
    Artifact,
  };
}

function fileSource(
  name: string,
  url: string,
  maxBytes: number,
  companionIds: Record<string, string>,
  scope: ArtifactScope
): FileSource {
  let download: Promise<Uint8Array<ArrayBuffer>> | null = null;
  const bytes = () => {
    download ??= fetchUnderCap(url, maxBytes);
    return download;
  };
  return {
    name,
    url,
    bytes,
    text: async () => new TextDecoder().decode(await bytes()),
    companions: Object.keys(companionIds),
    companion: async (companionName) => {
      const id = companionIds[companionName];
      if (!id) {
        throw new Error(
          `${companionName} was not retrieved with ${name}. Retrieve the record's header from its dataset so its files come with it.`
        );
      }
      const path = artifactPreviewByIdUrl(scope, id);
      if (!path) {
        throw new Error(`${companionName} cannot be read outside a conversation.`);
      }
      const companionUrl = urlFromPreview(await apiClient<ArtifactPreview>(path));
      if (!companionUrl) {
        throw new Error(`${companionName} has no readable URL.`);
      }
      return companionUrl;
    },
  };
}

async function fetchUnderCap(url: string, maxBytes: number): Promise<Uint8Array<ArrayBuffer>> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Server returned ${response.status}`);
  }
  const declared = Number(response.headers.get("Content-Length"));
  if (declared > maxBytes) {
    throw tooLargeError(declared, maxBytes);
  }
  const body = new Uint8Array(await response.arrayBuffer());
  if (body.byteLength > maxBytes) {
    throw tooLargeError(body.byteLength, maxBytes);
  }
  return body;
}

export function tooLargeError(size: number, maxBytes: number): Error {
  const mb = (n: number) => `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return new Error(
    `File is too large to render inline (${mb(size)}; the limit is ${mb(maxBytes)}). Please download it instead.`
  );
}
